import { useMutation, useQuery } from "@tanstack/react-query";
import { App } from "antd";
import { useState } from "react";
import apiClient from "@/app/api/client";
import { usePaginatedQuery } from "@/shared/hooks/usePaginatedQuery";
import { getErrorMessage } from "@/shared/utils/getErrorMessage";
import { leadsService } from "../services/leads.service";
import type { Lead } from "../types";
import type { LeadFilters } from "./useLeadFilters";

/**
 * Filtrlarni so'rov parametrlariga aylantiradi.
 *
 * ⚠️ **Ro'yxat, xarita va eksport UCHALASI shu funksiyadan o'tadi** —
 * uch nusxa bo'lsa, «xaritada `flagged` ishlamaydi» sinfidagi jimgina
 * tafovut paydo bo'lardi (`buildZoneListParams` naqshi).
 */
export function buildLeadParams(filters: LeadFilters): Record<string, string> {
	const params: Record<string, string> = {};

	for (const [key, value] of Object.entries(filters)) {
		if (value === undefined || value === false || value === "") {
			continue;
		}

		params[key] = String(value);
	}

	return params;
}

export function useLeadsList(filters: LeadFilters) {
	return usePaginatedQuery<Lead>({
		queryKey: ["leads"],
		url: "/leads",
		params: buildLeadParams(filters),
	});
}

export function useLeadDetail(id: string | undefined) {
	return useQuery({
		queryKey: ["leads", "detail", id],
		queryFn: () => leadsService.detail(id ?? ""),
		enabled: Boolean(id),
	});
}

interface FormOption {
	id: string;
	name: string;
}

/**
 * Forma tanlash — F1 ning `GET /lead-forms` marshrutidan.
 *
 * ⚠️ Bu **F1 ning fayllariga tegish EMAS**: F2 uning HTTP yuzasini
 * brauzerdan o'qiydi, backend'dan emas. Spec §2.2 dagi taqiq
 * SERVER tomonidagi blokaro chaqiruvlarga tegishli — admin paneli
 * baribir ikkala resursni ham ko'rsatishi kerak.
 */
export function useLeadFormOptions() {
	return useQuery({
		queryKey: ["lead-forms", "options"],
		queryFn: async () => {
			const response = await apiClient.get<{
				success: true;
				data: { items: FormOption[] };
			}>("/lead-forms", { params: { page: 1, limit: 100 } });

			return response.data.data.items;
		},
		staleTime: 30_000,
	});
}

const EXPORT_POLL_MS = 2000;

/**
 * Eksportni so'raydi va tayyor bo'lguncha holatni so'rab turadi
 * (Q3 ochiq — SSE 4-bosqichda, hozircha polling; spec §14).
 *
 * ⚠️ Yuklab olish `apiClient` orqali blob sifatida — oddiy havola
 * Bearer tokenni qo'sha olmaydi (access token faqat JS xotirasida).
 */
export function useLeadExport() {
	const { message } = App.useApp();
	const [jobId, setJobId] = useState<string | null>(null);

	const status = useQuery({
		queryKey: ["leads", "export", jobId],
		queryFn: () => leadsService.exportStatus(jobId ?? ""),
		enabled: Boolean(jobId),
		refetchInterval: (query) => {
			const data = query.state.data;

			return data && (data.status === "done" || data.status === "failed") ? false : EXPORT_POLL_MS;
		},
	});

	const request = useMutation({
		mutationFn: (body: Record<string, unknown>) => leadsService.requestExport(body),
		onSuccess: (job) => {
			setJobId(job.id);
			message.info("Eksport tayyorlanmoqda");
		},
		onError: (error) => message.error(getErrorMessage(error, "Eksportni boshlab bo'lmadi")),
	});

	async function download(): Promise<void> {
		if (!jobId) {
			return;
		}

		try {
			const blob = await leadsService.download(jobId);
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");

			link.href = url;
			link.download = `lidlar-${jobId}.csv`;
			link.click();

			URL.revokeObjectURL(url);
		} catch (error) {
			message.error(getErrorMessage(error, "Faylni yuklab bo'lmadi"));
		}
	}

	return { request, status: status.data, isPolling: status.isFetching, download };
}
